import { FileText } from "lucide-react";

import type { OpenDocumentRequest } from "@/components/assistant/types";
import { useProject } from "@/hooks/queries/useProject";

/**
 * Provenance chip for a chat started "from" a document: "From: <filename>", opening the source
 * in the inspector. Renders nothing if the source can't be resolved (e.g. the document was
 * deleted — the server nulls the link, so the chip degrades away gracefully).
 */
export function SourceDocumentChip({
  projectId,
  sourceDocumentId,
  onOpen,
}: {
  projectId: string;
  sourceDocumentId: string;
  onOpen: (request: OpenDocumentRequest) => void;
}) {
  const { data: project } = useProject(projectId);
  const doc = project?.documents.find((item) => item.id === sourceDocumentId);
  if (!doc) return null;

  return (
    <button
      type="button"
      onClick={() => onOpen({ kind: "document", documentId: doc.id, filename: doc.filename, versionId: doc.current_version_id ?? undefined, versionNumber: doc.latest_version_number ?? undefined })}
      className="flex items-center gap-1.5 rounded-full border bg-muted/50 px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted"
      title={`Started from ${doc.display_name ?? doc.filename}`}
    >
      <FileText className="size-3.5 shrink-0" aria-hidden />
      <span className="max-w-40 truncate">From: {doc.display_name ?? doc.filename}</span>
    </button>
  );
}
